import { Button, Flex, useStyleConfig } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/react/sortable";
import React, { type PropsWithChildren } from "react";
import { TooltipStyled } from "@/components/atoms";
import { CloseIcon, DragIcon } from "@/components/icons";

type Props = {
  id: number | string;
  index: number;
  disabled?: boolean;
  active?: boolean;
  default?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  tooltip?: string;
} & PropsWithChildren;

export const ButtonDnd = React.memo(function ButtonDnd(props: Props) {
  const handleRef = React.useRef(null);
  const { bg } = useStyleConfig("Button", { variant: "solid" });
  const { ref, isDragging } = useSortable({
    id: props.id,
    index: props.index,
    handle: handleRef,
  });

  const customBg = props.active ? "brand.500" : bg;
  const bgHover = props.active ? "brand-hover" : undefined;

  return (
    <Flex
      justifyContent={"center"}
      borderRadius={"12px"}
      ref={ref}
      background={typeof customBg === "string" ? customBg : undefined}
      color={props.active ? "white" : undefined}
      outlineOffset={"3px"}
      outline={
        props.default && !isDragging ? "1px solid var(--chakra-colors-brand-500)" : undefined
      }
    >
      <Button
        size={"sm"}
        ref={handleRef}
        p={0}
        variant={"link"}
        minW={"20px"}
        px={"5px"}
        cursor={"grab"}
        color={"inherit"}
      >
        <DragIcon />
      </Button>
      <TooltipStyled isDisabled={!props.default} tooltipText={props.tooltip} placement="top">
        <Button
          onClick={props.onClick}
          size={"sm"}
          background={"transparent"}
          color={"inherit"}
          _hover={bgHover && { bg: bgHover }}
        >
          {props.children}
        </Button>
      </TooltipStyled>

      <Button
        size={"sm"}
        variant={"link"}
        onClick={props.onRemove}
        p={0}
        minW={"20px"}
        px={"5px"}
        color={"inherit"}
        _hover={{
          color: "red.500",
          _dark: { color: "red.200" },
        }}
      >
        <CloseIcon size={18} />
      </Button>
    </Flex>
  );
});
